import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { ProductType } from '@tink/core/src/types';
import { CredentialsRequest } from '../../../../../../lib-api/services/Credentials';
import {
  PaymentProviderConditions,
  PaymentProviderDetailsModel,
} from '../../../../models/payments';
import { ProviderEntity } from '../../../../../../models/provider';
import {
  containsCredentials,
  createCredentials as createCredentialsImport,
  refreshCredentials as refreshCredentialsImport,
  updateCredentials as updateCredentialsImport,
} from '../../../../../../connectivity/services/credentials';
import { Credentials, CredentialsEntity } from '../../../../../../models/credentials';
import { RefreshableItem } from '../../../../../../models/refreshableItem';
import { applicationParameters } from '../../../../../../utilities/application-parameters';

import { SubmitProviderInputsPayload } from '../../../../../../components/screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { AppThunk } from '../../../../../../stores/redux-store';
import {
  haveFeatureFlag,
  navigateUserToInitialTinkLinkAfterThirdPartyRedirect,
  shouldRedirectToReferPage,
} from '../../../../../../models/configuration';
import { getCredentialsAppUri } from '../../../../../../utilities/app-uri/app-uri';
import { ProductDimensionPay, ProductNames } from '../../../../../../models/product';
import { getPaymentRefreshableItems } from '../../../../../helpers/fraud-prevention';

export type OnSubmitCredentialsProps = {
  fields: SubmitProviderInputsPayload;
  provider: ProviderEntity;
  configurationData: ConfigurationModel;
  userCredentials: CredentialsEntity[];
  credentials?: CredentialsEntity;
  credentialsRequestBase?: Partial<CredentialsRequest>;
  providerPaymentConditions?: PaymentProviderDetailsModel;
  createCredentials?: typeof createCredentialsImport;
  updateCredentials?: typeof updateCredentialsImport;
  refreshCredentials?: typeof refreshCredentialsImport;
  containsExistingCredentials?: typeof containsCredentials;
  canSkipAIS: boolean;
};

export const onSubmitCredentialsPay =
  ({
    fields,
    provider,
    configurationData,
    userCredentials,
    credentials,
    credentialsRequestBase,
    providerPaymentConditions,
    createCredentials = createCredentialsImport,
    refreshCredentials = refreshCredentialsImport,
    updateCredentials = updateCredentialsImport,
    containsExistingCredentials = containsCredentials,
    canSkipAIS,
  }: OnSubmitCredentialsProps): AppThunk =>
  async (dispatch) => {
    if (!provider) {
      return;
    }

    let instantSign = false;
    let refreshableItems: RefreshableItem[] = [];
    let authenticate = applicationParameters.get('authenticate');
    const productNames = [ProductNames.PRODUCT_PAYMENT_INITIATION];
    const credentialsRequest = {
      ...credentialsRequestBase,
      fields,
      appUri: getCredentialsAppUri({
        redirectToReferPage: shouldRedirectToReferPage(configurationData),
        navigateToInitialTinkLinkRedirect:
          navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
      }),
      providerName: provider.name,
      triggerRefresh: false,
      productNames,
    };

    if (
      !PaymentProviderConditions.isSourceAccountRequiredBeforeRedirect(providerPaymentConditions) ||
      canSkipAIS
    ) {
      // PIS ONLY, also for isSourceAccountProvidedSkipsAis with SourceAccount
      refreshableItems = [];
      credentialsRequest.triggerRefresh = false;
      instantSign = false;
    } else {
      refreshableItems = getPaymentRefreshableItems(
        ProductType.PAY,
        ProductDimensionPay.DIRECT,
        configurationData
      );
      credentialsRequest.triggerRefresh = true;
      instantSign = true;
    }

    const shouldValidateCredentialsConsent =
      // temp feature flag for internal testing, remove when confirmed that logic works as intended
      haveFeatureFlag(configurationData, ConfigurationFeatureFlag.PERMANENT_USER_1SCA);

    if (shouldValidateCredentialsConsent) {
      authenticate = true;
    }

    // We can only update credentials which are in ERROR state.
    // If we try to update credentials in CREATED or UPDATED state we get 400 response back because `username` field is immutable.
    if (credentials && Credentials.hasError(credentials)) {
      dispatch(updateCredentials(credentials.id, credentialsRequest));

      return;
    }

    // If we have already existing credentials we want to refresh these instead
    const alreadyExistingCredentials = containsExistingCredentials(
      userCredentials,
      credentialsRequest
    );

    if (alreadyExistingCredentials) {
      dispatch(
        refreshCredentials({
          credentials: alreadyExistingCredentials,
          authenticate,
          refreshableItems,
          appUri: getCredentialsAppUri({
            redirectToReferPage: shouldRedirectToReferPage(configurationData),
            navigateToInitialTinkLinkRedirect:
              navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
          }),
          productNames,
          ...credentialsRequestBase,
        })
      );

      return;
    }

    if (credentials && containsExistingCredentials([credentials], credentialsRequest)) return;

    // Otherwise we want to create new credentials if we do not already have them
    dispatch(
      createCredentials({
        credentialsRequest,
        refreshableItems,
        instantSign,
      })
    );
  };
